import React, {
  lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import useUser from '../../hooks/useUser';
import {
  sessionsChangeClientId,
  sessionsCreateWithToken,
  sessionsDestroy,
  verifyToken,
} from '../../services/Sessions';

import { STATUS_PENDING } from '../../assets/AppConstants';
import { HTTP_STATUS_6XX } from '../../assets/constants/Http';

import { getDefaultLanguage } from '../../utils/language';

import { useNavigateSearchPublic } from '../../hooks/useNavigate';

import { acceptLegal, getLegalTemplate } from '../../services/Legal';
import { getUserProfile } from '../../services/Users';

import { useAppContext } from '../app';
import { useAnalyticsContext } from '../analytics';

import UserContext from './context';

const ChangeClient = lazy(() => import('../../views/base/ChangeClient'));
const AccountVerificationDialog = lazy(() => import('../../modern/auth/components/AccountVerificationDialog')
  .then((module) => ({ default: module.AccountVerificationDialog })));
const TermsAndConditions = lazy(() => import('../../views/public/TermsAndConditions')
  .then((module) => ({ default: module.TermsAndConditions })));
const ActivateWorkspacePage = lazy(() => import('../../views/public/ActivateWorkspace')
  .then((module) => ({ default: module.ActivateWorkspacePage })));

const hasQueuedWorkspaces = () => {
  try {
    const queue = JSON.parse(window.localStorage.getItem('workspaceQueue') ?? '[]');
    return Array.isArray(queue) && queue.length > 0;
  } catch (_error) {
    return false;
  }
};
interface UserContextProviderProps {
  children: ReactNode;
}

const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const { user, setUser, emptyUser } = useUser();

  const { blockScreen, handleAlert, handleResetStatus } = useAppContext();
  const { resetAnalytics, initAnalytics } = useAnalyticsContext();
  const navigateSearch = useNavigateSearchPublic();

  const [legalHtml, setLegalHtml] = useState<any>(null);
  const [openWorkspace, setOpenWorkspace] = useState(false);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [openVerification, setOpenVerification] = useState(false);
  const [mfaData, setMfaData] = useState<any>();
  const [ssoChallenge, setSsoChallenge] = useState<any>(null);
  const tokenLoginInFlight = useRef<any>(false);

  // userId and registrationDate are no longer returned by the login response (DNK-8277 / F-08):
  // they are not needed to authenticate. We fetch them from the authenticated profile endpoint so
  // analytics (Mixpanel/Hotjar identify) and the survey timing keep working.
  const withProfileData = async (session) => {
    try {
      const profile = await getUserProfile();
      return {
        ...session,
        userId: profile?.id,
        registrationDate: profile?.registrationDate,
      };
    } catch {
      return session;
    }
  };

  const userIsLogged = () => Boolean(user?.status);
  const getUserStatus = () => user?.status;
  const getUserRole = () => user?.role;

  const logoutUser = async () => {
    setUser(emptyUser);
    resetAnalytics();
    handleResetStatus();
  };

  const handleOpenLegal = async () => {
    blockScreen(true);

    try {
      const template = await getLegalTemplate();
      setLegalHtml(template);
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const handlePasswordExpired = (error) => {
    const { token, code } = error.response.data;
    if (code === HTTP_STATUS_6XX.PASSWORD_EXPIRED) {
      navigateSearch(`/reset-password/${token}`, { passwordExpired: true });
    } else {
      handleAlert(error.response.data.message, 'error');
    }
  };

  const handleSessionEstablished = async (session) => {
    const { status, legalAccepted } = session;

    session.language ??= getDefaultLanguage().id;

    const enriched = await withProfileData(session);
    setUser({ ...enriched, openLegal: !legalAccepted });
    initAnalytics(enriched);
    setOpenWorkspace(status !== STATUS_PENDING && hasQueuedWorkspaces());
    setOpenVerification(status === STATUS_PENDING);
    if (!legalAccepted) {
      handleOpenLegal();
    }
  };

  const handleLoginWithToken = async ({ idToken, refreshToken, token }) => {
    if (tokenLoginInFlight.current) return true;
    tokenLoginInFlight.current = true;
    const data = { idToken, refreshToken, token };
    blockScreen(true);
    try {
      const session = await sessionsCreateWithToken(data, token);
      const {
        status,
        clientId,
        qrCode,
        needsToken,
        legalAccepted,
      } = session;

      const defaultLanguage = getDefaultLanguage();

      session.language ??= defaultLanguage.id;

      if (clientId) {
        const enriched = await withProfileData(session);
        setUser({
          ...user,
          ...enriched,
          external: true,
          authenticated: true,
          openLegal: !legalAccepted,
        });
        initAnalytics({
          ...user,
          ...enriched,
          external: true,
        });
        setOpenWorkspace(status !== STATUS_PENDING && hasQueuedWorkspaces());
        setOpenVerification(status === STATUS_PENDING);
        if (!legalAccepted) {
          handleOpenLegal();
        }
      } else {
        setMfaData({
          qrCode,
          needsToken,
          external: true,
        });
        setSsoChallenge({ qrCode: qrCode ?? null, needsToken: Boolean(needsToken) });
      }
      return true;
    } catch {
      // The user authenticated in Keycloak but the session could not be created
      // (e.g. the email is not registered in Datanomik). Reset the persisted SSO
      // state so the login-required loop is broken; the Keycloak provider handles
      // the Keycloak logout and the error message.
      setUser(emptyUser);
      return false;
    } finally {
      tokenLoginInFlight.current = false;
      blockScreen(false);
    }
  };

  const clearSsoChallenge = () => setSsoChallenge(null);

  const handleCloseClientModal = async () => {
    setOpenClientModal(false);
  };

  const handleOpenClientModal = () => setOpenClientModal(true);

  const handleChangeClientId = async ({ clientId, clientName }) => {
    blockScreen(true);

    try {
      const session = await sessionsChangeClientId(clientId);
      const enriched = await withProfileData(session);

      const finalSession = {
        ...user,
        ...enriched,
        clientName,
        multiClient: true,
        authenticated: true,
        openLegal: !session.legalAccepted,
      };
      setUser(finalSession);
      initAnalytics(finalSession);
      handleCloseClientModal();

      if (!session.legalAccepted) {
        handleOpenLegal();
      }
    } catch (error) {
      setOpenClientModal(false);
      handlePasswordExpired(error);
    } finally {
      blockScreen(false);
    }
  };

  const handleVerifyTokenAndChangeClientId = async ({
    device,
    trustDevice,
    mfaToken,
  }) => {
    blockScreen(true);

    let valid = { isValid: false, hasClientId: false };

    try {
      const session = await verifyToken({
        token: mfaToken,
        device: trustDevice ? device : undefined,
      });

      valid = { isValid: true, hasClientId: Boolean(session.clientId) };

      if (session.clientId) {
        const isLogged = userIsLogged();
        const enriched = await withProfileData(session);

        const finalSession = {
          ...user,
          ...enriched,
          ...(mfaData?.external ? { external: true, authenticated: true } : {}),
        };
        setUser(finalSession);
        initAnalytics(finalSession);
        handleCloseClientModal();

        if (isLogged) {
          window.location.reload();
        }
      }
    } catch (error) {
      if (error?.response?.data?.code === HTTP_STATUS_6XX.SESSION_EXPIRED) {
        setUser(emptyUser);
        window.location.reload();
      } else {
        handleAlert(error?.response?.data?.message || error, 'error');
      }
    } finally {
      blockScreen(false);
    }

    return valid;
  };

  useEffect(() => setOpenVerification(user.status === STATUS_PENDING), [user]);

  const handleLogout = async () => {
    await sessionsDestroy();
    logoutUser();
  };

  const handleCloseVerification = () => {
    setOpenVerification(false);
  };

  const handleCloseWorkspace = () => setOpenWorkspace(false);

  const handleCloseLegalTemplate = () => {
    setUser(emptyUser);
  };

  const acceptLegalTemplate = async () => {
    blockScreen(true);
    try {
      await acceptLegal();
      handleAlert('Terms and Conditions accepted succefully!', 'success');
      setUser({ ...user, legalAccepted: true, openLegal: false });
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const userValue = useMemo(() => ({
    mfaData,
    ssoChallenge,
    clearSsoChallenge,
    openClientModal,
    userSession: user,
    setUserSession: setUser,
    userIsLogged,
    getUserStatus,
    getUserRole,
    logoutUser,
    handleLoginWithToken,
    handleLogout,
    handleChangeClientId,
    handleOpenClientModal,
    handleCloseClientModal,
    handleSessionEstablished,
    handleVerifyTokenAndChangeClientId,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, mfaData, openClientModal, ssoChallenge]);

  if (userIsLogged() && user?.openLegal && legalHtml) {
    return (
      <Suspense fallback={null}>
        <TermsAndConditions
          openLegal={user?.openLegal}
          legalContent={legalHtml}
          onAccept={acceptLegalTemplate}
          onClose={handleCloseLegalTemplate}
        />
      </Suspense>
    );
  }

  return (
    <UserContext.Provider value={userValue}>
      {children}

      <Suspense fallback={null}>
        {openVerification && (
          <AccountVerificationDialog
            session={user}
            onVerified={handleCloseVerification}
            onCancel={handleCloseVerification}
          />
        )}

        {openWorkspace && (
          <ActivateWorkspacePage
            onClose={handleCloseWorkspace}
          />
        )}

        {openClientModal && <ChangeClient mode="public" />}
      </Suspense>

    </UserContext.Provider>
  );
};
export default UserContextProvider;
